// src/pages/AnnouncementsPage.js — V18: 告示 (announcements) + 変遷 (update log timeline).

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { apiClient } from '../api/apiClient';
import PageShell from '../components/PageShell';
import { CornerCard, SectionTitle, Tag, VersionTag, GhostButton, Muted } from '../components/ui';
import { color, font } from '../theme/tokens';

const LOG_PREVIEW = 8;

const Stack = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ $gap = 20 }) => $gap}px;
`;

const CardHead = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 18px;
    time { font-family: ${font.mono}; font-size: 12px; color: ${color.text3}; letter-spacing: 0.06em; }
`;

const CardTitle = styled.h3`
    margin: 0 0 14px;
    font-family: ${font.display};
    font-weight: 800;
    font-size: 28px;
    letter-spacing: 0.14em;
    color: ${color.text};
    @media (max-width: 640px) { font-size: 22px; }
`;

const Body = styled.p`
    margin: 0;
    font-size: 16px;
    line-height: 1.9;
    color: ${color.text2};
    white-space: pre-wrap;
    overflow-wrap: anywhere;
`;

const LogRow = styled.li`
    display: grid;
    grid-template-columns: 120px 40px minmax(0, 1fr) 120px;
    align-items: start;
    min-height: 96px;
    .ver { padding-top: 2px; }
    .rail { position: relative; align-self: stretch; display: flex; justify-content: center; }
    .rail::before { content: ''; position: absolute; top: 0; bottom: 0; width: 1px; background: ${color.line}; }
    .rail i {
        position: relative;
        margin-top: 9px;
        width: 8px;
        height: 8px;
        transform: rotate(45deg);
        background: ${color.void};
        border: 1px solid ${color.bloodHi};
    }
    .text { display: flex; flex-direction: column; gap: 8px; padding: 0 0 28px 8px; }
    .text h4 { margin: 0; font-family: ${font.display}; font-weight: 800; font-size: 19px; letter-spacing: 0.1em; color: ${color.text}; }
    .text p { margin: 0; font-size: 15px; line-height: 1.8; color: ${color.text2}; white-space: pre-wrap; overflow-wrap: anywhere; }
    time { text-align: right; padding-top: 5px; font-family: ${font.mono}; font-size: 12px; color: ${color.text3}; letter-spacing: 0.06em; }
    @media (max-width: 720px) {
        grid-template-columns: 24px minmax(0, 1fr);
        .ver { grid-column: 2; grid-row: 1; padding: 0 0 8px 8px; }
        .rail { grid-column: 1; grid-row: 1 / span 3; }
        .text { grid-column: 2; padding-bottom: 6px; }
        time { grid-column: 2; text-align: left; padding: 0 0 28px 8px; }
    }
`;

const LogList = styled.ol`
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
`;

const formatDate = (s) =>
    new Date(s).toLocaleDateString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(/\//g, '.');

const AnnouncementsPage = () => {
    const [announcements, setAnnouncements] = useState([]);
    const [updateLogs, setUpdateLogs] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showAll, setShowAll] = useState(false);

    useEffect(() => {
        const fetchInfo = async () => {
            setIsLoading(true);
            try {
                const response = await apiClient.get('/public/info');
                setAnnouncements(response.data.announcements || []);
                setUpdateLogs(response.data.updateLogs || []);
            } catch (error) {
                console.error('Failed to fetch public info:', error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchInfo();
    }, []);

    const logs = showAll ? updateLogs : updateLogs.slice(0, LOG_PREVIEW);
    const hidden = updateLogs.length - LOG_PREVIEW;

    return (
        <PageShell num="壱" title="公告" en="NEWS" quote="そう、嘘なのだ！すべては嘘であったのだ！" sideLabel="告示">
            {isLoading ? (
                <Muted>载入中…</Muted>
            ) : (
                <Stack $gap={72}>
                    <Stack>
                        <SectionTitle jp="告示" cn="公告" />
                        {announcements.length > 0 ? announcements.map((item, i) => (
                            <CornerCard key={item.id} pad="36px 40px">
                                <CardHead>
                                    <div style={{ display: 'flex', gap: 12 }}>
                                        {i === 0 && <Tag $solid>最新</Tag>}
                                    </div>
                                    <time dateTime={item.created_at}>{formatDate(item.created_at)}</time>
                                </CardHead>
                                <CardTitle>{item.title}</CardTitle>
                                <Body>{item.content}</Body>
                            </CornerCard>
                        )) : <Muted>暂无公告</Muted>}
                    </Stack>

                    <Stack $gap={28}>
                        <SectionTitle jp="変遷" cn="版本日志" />
                        {updateLogs.length > 0 ? (
                            <LogList>
                                {logs.map((item) => (
                                    <LogRow key={item.id}>
                                        <div className="ver"><VersionTag>{item.version}</VersionTag></div>
                                        <div className="rail"><i /></div>
                                        <div className="text">
                                            <h4>{item.title}</h4>
                                            {item.content && item.content !== item.title && <p>{item.content}</p>}
                                        </div>
                                        <time dateTime={item.created_at}>{formatDate(item.created_at)}</time>
                                    </LogRow>
                                ))}
                            </LogList>
                        ) : <Muted>暂无日志</Muted>}
                        {hidden > 0 && (
                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                                <GhostButton type="button" onClick={() => setShowAll((v) => !v)}>
                                    {showAll ? '收起' : `展开更早的 ${hidden} 条`}
                                </GhostButton>
                            </div>
                        )}
                    </Stack>
                </Stack>
            )}
        </PageShell>
    );
};

export default AnnouncementsPage;
